import type { User } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";

import { ApiError } from "./api-error";

export async function requireAuthenticatedUser(): Promise<User> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new ApiError("not_authenticated");
  }

  return user;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function isAccessTokenStillValid(accessToken: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.getUser(accessToken);
    return !error && Boolean(data.user);
  } catch (error) {
    console.warn("Transient access token validation failure, keeping the current session.", error);
    return true;
  }
}

export async function getValidAccessToken(): Promise<string> {
  const refreshAccessToken = async (refreshToken?: string | null): Promise<string> => {
    const refreshed = refreshToken
      ? await supabase.auth.refreshSession({ refresh_token: refreshToken })
      : await supabase.auth.refreshSession();
    const refreshedSession = refreshed.data.session ?? null;

    if (refreshed.error || !refreshedSession?.access_token) {
      throw new ApiError("not_authenticated");
    }

    const refreshedTokenValid = await isAccessTokenStillValid(refreshedSession.access_token);
    if (!refreshedTokenValid) {
      throw new ApiError("not_authenticated");
    }

    return refreshedSession.access_token;
  };

  const maxAttempts = 2;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const session = (await supabase.auth.getSession()).data.session;

    if (!session) {
      throw new ApiError("not_authenticated");
    }

    const expiresAtMs = typeof session.expires_at === "number" ? session.expires_at * 1000 : null;
    const expiresSoon = expiresAtMs != null && expiresAtMs - Date.now() <= 60_000;

    if (session.access_token && !expiresSoon && (await isAccessTokenStillValid(session.access_token))) {
      return session.access_token;
    }

    try {
      return await refreshAccessToken(session.refresh_token);
    } catch (error) {
      if (session.access_token && (await isAccessTokenStillValid(session.access_token))) {
        return session.access_token;
      }

      if (attempt === maxAttempts - 1) {
        throw error;
      }

      await wait(250 * (attempt + 1));
    }
  }

  throw new ApiError("not_authenticated");
}
